import React from "react";
import { useTranslate } from "@refinedev/core";
import { DateField } from "@refinedev/antd";
import { Flex, Avatar, Divider, Typography, theme } from "antd";
import { ClockCircleOutlined, CarOutlined, CodeOutlined, AppstoreOutlined } from "@ant-design/icons";
import { useAvatarPalette } from "../../../../shared/styles";
import { getAvatarColor, getInitialsFromName } from "../../../../shared/utils";

export interface OrderShowHeaderProps {
    order: any;
    field: (name: string) => { value?: string; label?: string } | undefined;
    isMobile?: boolean;
}

export const OrderShowHeader: React.FC<OrderShowHeaderProps> = ({ order, field, isMobile = false }) => {
    const _t = useTranslate();
    const { token } = theme.useToken();
    const avatarPalette = useAvatarPalette();

    if (isMobile) {
        return (
            <div className="space-y-3 pt-6">
                {/* Account row */}
                <Flex align="center" gap="small" className="mb-6">
                    <Avatar
                        shape="square"
                        size={36}
                        style={{
                            backgroundColor: getAvatarColor(order.account.id, avatarPalette),
                            fontWeight: 600,
                            flexShrink: 0,
                        }}
                    >
                        {getInitialsFromName(order.account.name)}
                    </Avatar>
                    <div>
                        <div className="font-medium text-sm" style={{ color: token.colorText }}>
                            {order.account?.name}
                        </div>
                        <div className="text-xs" style={{ color: token.colorTextSecondary }}>
                            {_t("orders.fields.account")}
                        </div>
                    </div>
                </Flex>

                {/* 2-column grid for remaining fields */}
                <div className="grid grid-cols-2 gap-x-3 gap-y-3">
                    <Flex gap="small" align="flex-start">
                        <ClockCircleOutlined style={{ color: token.colorPrimary, marginTop: 2, flexShrink: 0 }} />
                        <div className="min-w-0">
                            <div className="text-sm font-medium" style={{ color: token.colorText }}>
                                <DateField value={order.createdAt} format="DD MMM HH:mm" />
                            </div>
                            <div className="text-xs" style={{ color: token.colorTextSecondary }}>
                                {_t("orders.fields.createdAt")}
                            </div>
                        </div>
                    </Flex>

                    <Flex gap="small" align="flex-start">
                        <CarOutlined style={{ color: token.colorPrimary, marginTop: 2, flexShrink: 0 }} />
                        <div className="min-w-0">
                            <div className="text-sm font-medium truncate" style={{ color: token.colorText }}>
                                {field("make")?.value} {field("model")?.value}
                            </div>
                            <div className="text-xs" style={{ color: token.colorTextSecondary }}>
                                {_t("orders.fields.make")} / {_t("orders.fields.model")}
                            </div>
                        </div>
                    </Flex>

                    <Flex gap="small" align="flex-start">
                        <CodeOutlined style={{ color: token.colorPrimary, marginTop: 2, flexShrink: 0 }} />
                        <div className="min-w-0">
                            <div className="text-sm font-medium break-all" style={{ color: token.colorText }}>
                                {field("vin")?.value}
                            </div>
                            <div className="text-xs" style={{ color: token.colorTextSecondary }}>
                                {_t("orders.fields.vin")}
                            </div>
                        </div>
                    </Flex>

                    <Flex gap="small" align="flex-start">
                        <AppstoreOutlined style={{ color: token.colorPrimary, marginTop: 2, flexShrink: 0 }} />
                        <div className="min-w-0">
                            <div className="text-sm font-medium" style={{ color: token.colorText }}>
                                {order.products?.map((product: any) => product.name).join(", ")}
                            </div>
                            <div className="text-xs" style={{ color: token.colorTextSecondary }}>
                                {_t("orders.fields.products")}
                            </div>
                        </div>
                    </Flex>
                </div>
            </div>
        );
    }

    return (
        <div
            key={order.id}
            className="shadow-none rounded-none border-r-0 border-l-0 border-t-0 border-b-0"
        >
            <div className="space-y-3">
                <Flex justify="space-between">
                    <Flex gap="large">
                        <Flex align="flex-start" gap="middle" className="min-w-[256px]">
                            <Avatar
                                shape="square"
                                size={44}
                                style={{
                                    backgroundColor: getAvatarColor(order.account.id, avatarPalette),
                                    fontWeight: 600,
                                }}
                            >
                                {getInitialsFromName(order.account.name)}
                            </Avatar>
                            <div>
                                <div className="font-medium" style={{ color: token.colorText }}>
                                    {order.account?.name}
                                </div>
                                <div className="text-sm" style={{ color: token.colorTextSecondary }}>
                                    {_t("orders.fields.account")}
                                </div>
                            </div>
                        </Flex>

                        <Divider type="vertical" style={{ height: "auto", margin: "0" }} />

                        <Flex align="flex-start" gap="small">
                            <div>
                                <ClockCircleOutlined className="text-base" style={{ color: token.colorPrimary }} />
                            </div>
                            <div>
                                <div className="font-medium text-sm" style={{ color: token.colorText }}>
                                    <DateField className="font-medium" value={order.createdAt} format="DD MMM HH:mm" />
                                </div>
                                <div className="text-sm" style={{ color: token.colorTextSecondary }}>
                                    {_t("orders.fields.createdAt")}
                                </div>
                            </div>
                        </Flex>

                        <Divider type="vertical" style={{ height: "auto", margin: "0" }} />

                        <Flex align="flex-start" gap="middle">
                            <div>
                                <CarOutlined className="text-base" style={{ color: token.colorPrimary }} />
                            </div>
                            <div>
                                <div className="font-medium" style={{ color: token.colorText }}>
                                    {field("make")?.value} {field("model")?.value}
                                </div>
                                <div className="text-sm" style={{ color: token.colorTextSecondary }}>
                                    {_t("orders.fields.make")} / {_t("orders.fields.model")}
                                </div>
                            </div>
                        </Flex>

                        <Divider type="vertical" style={{ height: "auto", margin: "0" }} />

                        <Flex align="flex-start" gap="middle">
                            <div>
                                <CodeOutlined className="text-base" style={{ color: token.colorPrimary }} />
                            </div>
                            <div>
                                <div className="font-medium" style={{ color: token.colorText }}>
                                    {field("vin")?.value}
                                </div>
                                <div className="text-sm" style={{ color: token.colorTextSecondary }}>
                                    {_t("orders.fields.vin")}
                                </div>
                            </div>
                        </Flex>

                        <Divider type="vertical" style={{ height: "auto", margin: "0" }} />

                        <Flex align="flex-start" gap="middle">
                            <div>
                                <AppstoreOutlined className="text-base" style={{ color: token.colorPrimary }} />
                            </div>
                            <div className="flex-1">
                                <div className="font-medium" style={{ color: token.colorText }}>
                                    {order.products?.map((product: any) => product.name).join(", ")}
                                </div>
                                <div className="text-sm" style={{ color: token.colorTextSecondary }}>
                                    {_t("orders.fields.products")}
                                </div>
                            </div>
                        </Flex>
                    </Flex>
                </Flex>
            </div>
        </div>
    );
};
